import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useNavigate, useParams, useLocation, useBlocker } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';
import '../../styles/Audit.css';
import WalterLoader from '../shared/WalterLoader';
import { FaEdit, FaTrash, FaClone } from 'react-icons/fa';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Swal from 'sweetalert2';
import ReactSwitch from 'react-switch';

import { generateFunctionalUnitId }  from './auditSubsections/utils';
import FunctionalUnitCascadeModal from '../shared/FunctionalUnitCascadeModal';

import WaterpointForm from './WaterpointForm';
import ReorganizeView from './ReorganizeView';
import DuplicateZoneModal from './DuplicateZoneModal';
import NotesAndPhotosSection from './auditSubsections/NotesAndPhotosSection';

import { useOnlineStatus } from '../../offline/ConnectivityContext';
import { useSyncOnReconnect } from '../../offline/useSyncOnReconnect';
import { usePendingSyncIds } from '../../offline/usePendingSyncIds';
import { useStorageEstimate } from '../../offline/useStorageEstimate';
import { createSection, createRoom } from '../../offline/createApi';
import { useBeforeUnloadWarning } from '../shared/useBeforeUnloadWarning';

const formatMB = (bytes) => `${Math.round(bytes / (1024 * 1024))} Mo`;

function AuditPageCreationSectionsAndRooms() {

    // CONST
    const navigate = useNavigate();
    const { id_client, id_building } = useParams();
    const location = useLocation();
    const { initialSectionId = null, initialRoomId = null } = location.state || {};
    const { isOnline } = useOnlineStatus();
    const pendingSyncIds = usePendingSyncIds();

    const [error, setError] = useState(null);
    const [sections, setSections] = useState([]);
    const [rooms, setRooms] = useState([]);
    const [equipments, setEquipments] = useState([]);

    const [dataClient, setDataClient] = useState(null);
    const [buildingData, setBuildingData] = useState(null);

    const [selectedSection, setSelectedSection] = useState(null);
    const [selectedRoom, setSelectedRoom] = useState(null);

    const [showSectionPopup, setShowSectionPopup] = useState(false);
    const [showRoomPopup, setShowRoomPopup] = useState(false);

    const [sectionNameForm, setSectionNameForm] = useState(null)
    const [roomNameForm, setRoomNameForm] = useState(null)

    const [loading, setLoading] = useState(true);

    const [showEditSectionPopup, setShowEditSectionPopup] = useState(false);
    const [showEditRoomPopup, setShowEditRoomPopup] = useState(false);
    const [editSectionNameForm, setEditSectionNameForm] = useState(null);
    const [editRoomNameForm, setEditRoomNameForm] = useState(null);

    const [editSectionActiveTab, setEditSectionActiveTab] = useState('info');
    const [editRoomActiveTab, setEditRoomActiveTab] = useState('info');
    const [editSectionNotes, setEditSectionNotes] = useState('');
    const [editSectionPhotos, setEditSectionPhotos] = useState([]);
    const [editRoomNotes, setEditRoomNotes] = useState('');
    const [editRoomPhotos, setEditRoomPhotos] = useState([]);

    // { functionalUnitId, rooms, usages } le temps que l'utilisateur choisisse de propager
    // (ou non) un changement d'unité fonctionnelle aux pièces/usages enfants.
    const [sectionCascadePrompt, setSectionCascadePrompt] = useState(null);
    const [roomCascadePrompt, setRoomCascadePrompt] = useState(null);

    const [showDuplicatePopup, setShowDuplicatePopup] = useState(false);
    const [duplicateSection, setDuplicateSection] = useState(null);
    const [duplicateRooms, setDuplicateRooms] = useState([]);
    const [duplicateRoom, setDuplicateRoom] = useState(null);
    const [selectedEquipment, setSelectedEquipment] = useState(null);

    const [showEquipmentSheet, setShowEquipmentSheet] = useState(false);
    const [sheetSectionId, setSheetSectionId] = useState(null);
    const [sheetRoomId, setSheetRoomId] = useState(null);
    const [sheetEquipmentId, setSheetEquipmentId] = useState(null);
    const [sheetKey, setSheetKey] = useState(0);
    const [sheetCanSave, setSheetCanSave] = useState(false);
    const [sheetIsSaving, setSheetIsSaving] = useState(false);
    const waterpointRef = useRef(null);

    const [reorganizeMode, setReorganizeMode] = useState(false);
    const [duplicateZone, setDuplicateZone] = useState(null); // null | { kind: 'room' | 'section', item }

    const [roomNbIdenticalRooms, setRoomNbIdenticalRooms] = useState(1);
    const [editSectionPersonCount, setEditSectionPersonCount] = useState(null);
    const [editRoomPersonCount, setEditRoomPersonCount] = useState(null);
    const [editRoomNbIdenticalRooms, setEditRoomNbIdenticalRooms] = useState(1);
    const [editSectionFunctionalUnit, setEditSectionFunctionalUnit] = useState(null);
    const [sectionFunctionalUnit, setSectionFunctionalUnit] = useState('');
    const [newRoomFunctionalUnit, setNewRoomFunctionalUnit] = useState("");


    const [editSectionFrequentation, setEditSectionFrequentation] = useState('average');
    const [editRoomFrequentation, setEditRoomFrequentation] = useState('average');

    const [roomHasAwarenessPoster, setRoomHasAwarenessPoster] = useState(false);
    const [editRoomHasAwarenessPoster, setEditRoomHasAwarenessPoster] = useState(false);

    const [roomFunctionalUnit, setRoomFunctionalUnit] = useState('');
    const [editRoomFunctionalUnit, setEditRoomFunctionalUnit] = useState('');
    const [newFunctionalUnit, setNewFunctionalUnit] = useState(""); // valeur du nouvel input unité fonctionnelle

    const translations = {
        wc: "WC",
        urinal: "Urinoirs",
        faucet: "Robinet",
        shower: "Douche",
        machine: "Machine"
    };

    // Functions

    const translate = (word) => {
        return translations[word] || word;
    };

    const backToBuildings = () => {
        navigate(`/audit/${id_client}`); 
    };
    const goToDashboard = () => {
        navigate(`/client/${id_client}/${id_building}`); 
    };

    const addEquipment = (id_section, id_room) => {
        setSheetSectionId(id_section);
        setSheetRoomId(id_room);
        setSheetEquipmentId(null);
        setSheetCanSave(false);
        setSheetKey(k => k + 1);
        setShowEquipmentSheet(true);
    };

    const handleEditEquipment = (id_section, id_room, id_equipment) => {
        setSheetSectionId(id_section);
        setSheetRoomId(id_room);
        setSheetEquipmentId(id_equipment);
        setSheetCanSave(false);
        setSheetKey(k => k + 1);
        setShowEquipmentSheet(true);
    };

    const handleSheetClose = async (savedEquipment) => {
        setShowEquipmentSheet(false);

        if (savedEquipment) {
            // Offline save: merge what WaterpointForm just persisted locally
            // instead of refetching (the GET below would fail offline anyway).
            setEquipments((prev) => [...prev.filter((eq) => eq._id !== savedEquipment._id), savedEquipment]);
        }

        if (!isOnline) return;

        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}/get_equipments_with_usages/${id_building}`);
            if (response.ok) setEquipments(await response.json());
        } catch (error) {
            console.error("Erreur lors du rafraîchissement des équipements :", error);
        }
    };

    const handleSectionClick = (section) => {
        setSelectedSection(section);
        const firstRoom = rooms.find(room => room.id_section === section._id);
        setSelectedRoom(firstRoom);
    };

    const handleSectionDoubleClick = (section) => {
        setSelectedSection(section);
        setEditSectionNameForm(section.name_section);
        setEditSectionPersonCount(section.person_count || 0);
        setEditSectionFrequentation(section.frequentation || 'average');
        setEditSectionFunctionalUnit(section.functional_unit || '')
        setEditSectionNotes(section.notes || '');
        setEditSectionPhotos(section.photos || []);
        setEditSectionActiveTab('info');
        setShowEditSectionPopup(true);
    };

    const handleRoomDoubleClick = (room) => {
        setSelectedRoom(room);
        setEditRoomNameForm(room.name_room);
        setEditRoomPersonCount(room.person_count || 0);
        setEditRoomNbIdenticalRooms(room.nb_identical_rooms || 1);
        setEditRoomFrequentation(room.frequentation || 'average');
        setEditRoomHasAwarenessPoster(room.has_awareness_poster || false);
        setEditRoomFunctionalUnit(room.functional_unit || '');
        setEditRoomNotes(room.notes || '');
        setEditRoomPhotos(room.photos || []);
        setEditRoomActiveTab('info');
        setShowEditRoomPopup(true);
    };

    // FONCTIONS AVEC API

    // Sélection courante, lue par fetchData() pour la conserver après un rafraîchissement
    // (sans en faire une dépendance du useCallback).
    const selectionRef = useRef({ sectionId: null, roomId: null });
    useEffect(() => {
        selectionRef.current = { sectionId: selectedSection?._id ?? null, roomId: selectedRoom?._id ?? null };
    }, [selectedSection, selectedRoom]);

    // `target` ({ sectionId, roomId }) force la sélection après rechargement (ex : élément
    // tout juste créé) ; sinon on garde la sélection courante si elle existe encore.
    const fetchData = useCallback(async (first = false, target = null) => {
        setLoading(true);
        try {
            const sectionsResponse = await fetch(`${process.env.REACT_APP_API_URL}/get_sections_from_building_id/${id_building}`);
            const roomsResponse = await fetch(`${process.env.REACT_APP_API_URL}/get_rooms_from_building_id/${id_building}`);
            const equipmentsResponse = await fetch(`${process.env.REACT_APP_API_URL}/get_equipments_with_usages/${id_building}`);

            if (sectionsResponse.ok && roomsResponse.ok && equipmentsResponse.ok) {
                const sectionsData = await sectionsResponse.json();
                const roomsData = await roomsResponse.json();
                const equipmentsData = await equipmentsResponse.json();

                setSections(sectionsData);
                setRooms(roomsData);
                setEquipments(equipmentsData);

                const wanted = first
                    ? { sectionId: initialSectionId, roomId: initialRoomId }
                    : (target || selectionRef.current);
                const section = sectionsData.find(s => s._id === wanted.sectionId) || sectionsData[0];
                const room = roomsData.find(r => r._id === wanted.roomId && r.id_section === section?._id)
                    || roomsData.find(r => r.id_section === section?._id);

                setSelectedSection(section);
                setSelectedRoom(room);
            } else {
                toast.error("Erreur lors de la récupération des données");
            }
        } catch (error) {
            toast.error("Erreur API")
            console.error("Erreur API:", error);
        } finally {
            setLoading(false);
        }
    }, [id_building, initialSectionId, initialRoomId]);

    // Drains the offline sync queue on reconnect, then reconciles local
    // (optimistic) state with the server via fetchData.
    const { syncProgress, queueCount, hasErrors: hasSyncErrors, authRequired: syncAuthRequired, retrySync } = useSyncOnReconnect(fetchData);
    const { supported: storageEstimateSupported, usageBytes, quotaBytes } = useStorageEstimate();

    // Any open popup means there's unsaved input (section/room name, edit
    // notes...) that an accidental reload/close/back would silently discard.
    // (Whole-page navigation while offline is handled separately — see the
    // disabled state on the Dashboard/Bâtiments buttons below, and the
    // app-wide OfflineUnloadGuard in App.js for reload/close regardless of
    // dirty state.)
    const hasUnsavedPopup = showSectionPopup || showRoomPopup || showEditSectionPopup || showEditRoomPopup;
    useBeforeUnloadWarning(hasUnsavedPopup);

    // Les champs de l'onglet "Compléments" (personnes, fréquentation, notes, photos)
    // ne sont persistés qu'au clic sur "Modifier" : on prévient avant de les perdre.
    const isSectionNotesDirty = showEditSectionPopup && !!selectedSection && (
        Number(editSectionPersonCount || 0) !== Number(selectedSection.person_count || 0) ||
        editSectionFrequentation !== (selectedSection.frequentation || 'average') ||
        editSectionNotes !== (selectedSection.notes || '') ||
        JSON.stringify(editSectionPhotos) !== JSON.stringify(selectedSection.photos || [])
    );
    const isRoomNotesDirty = showEditRoomPopup && !!selectedRoom && (
        Number(editRoomPersonCount || 0) !== Number(selectedRoom.person_count || 0) ||
        editRoomFrequentation !== (selectedRoom.frequentation || 'average') ||
        editRoomNotes !== (selectedRoom.notes || '') ||
        JSON.stringify(editRoomPhotos) !== JSON.stringify(selectedRoom.photos || [])
    );
    const hasUnsavedNotes = isSectionNotesDirty || isRoomNotesDirty;

    // Toute modification en cours (onglet Informations compris) : la duplication
    // part de la version enregistrée, on prévient avant de l'abandonner.
    const isSectionEditDirty = isSectionNotesDirty || (showEditSectionPopup && !!selectedSection && (
        (editSectionNameForm || '') !== (selectedSection.name_section || '') ||
        (editSectionFunctionalUnit || '') !== (selectedSection.functional_unit || '')
    ));
    const isRoomEditDirty = isRoomNotesDirty || (showEditRoomPopup && !!selectedRoom && (
        (editRoomNameForm || '') !== (selectedRoom.name_room || '') ||
        Number(editRoomNbIdenticalRooms || 1) !== Number(selectedRoom.nb_identical_rooms || 1) ||
        editRoomHasAwarenessPoster !== (selectedRoom.has_awareness_poster || false) ||
        (editRoomFunctionalUnit || '') !== (selectedRoom.functional_unit || '')
    ));

    const confirmDiscardNotes = async () => {
        const result = await Swal.fire({
            title: 'Modifications non enregistrées',
            html:
                'Les compléments (personnes, fréquentation, notes, photos) ont été modifiés mais pas enregistrés.<br>' +
                'Voulez-vous quitter sans enregistrer ?',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Quitter sans enregistrer',
            cancelButtonText: 'Rester',
            customClass: 'swal-custom'
        });
        return result.isConfirmed;
    };

    const closeEditSectionPopup = async () => {
        if (isSectionNotesDirty && !(await confirmDiscardNotes())) return;
        setShowEditSectionPopup(false);
    };

    const closeEditRoomPopup = async () => {
        if (isRoomNotesDirty && !(await confirmDiscardNotes())) return;
        setShowEditRoomPopup(false);
    };

    const startDuplicate = async (kind) => {
        const isDirty = kind === 'room' ? isRoomEditDirty : isSectionEditDirty;
        if (isDirty) {
            const result = await Swal.fire({
                title: 'Modifications non enregistrées',
                html: 'La copie part de la version enregistrée : les modifications en cours seront perdues.<br>Dupliquer quand même ?',
                icon: 'warning',
                showCancelButton: true,
                confirmButtonText: 'Dupliquer',
                cancelButtonText: 'Annuler',
                customClass: 'swal-custom'
            });
            if (!result.isConfirmed) return;
        }
        if (kind === 'room') {
            setShowEditRoomPopup(false);
            setDuplicateZone({ kind, item: selectedRoom });
        } else {
            setShowEditSectionPopup(false);
            setDuplicateZone({ kind, item: selectedSection });
        }
    };

    const handleDuplicateDone = (target) => {
        setDuplicateZone(null);
        fetchData(false, target);
    };

    // Retour arrière navigateur / navigation interne pendant l'édition.
    const blocker = useBlocker(
        ({ currentLocation, nextLocation }) =>
            hasUnsavedNotes && currentLocation.pathname !== nextLocation.pathname
    );

    useEffect(() => {
        if (blocker.state !== 'blocked') return;
        confirmDiscardNotes().then((confirmed) => {
            if (confirmed) blocker.proceed();
            else blocker.reset();
        });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [blocker]);

    useEffect(() => {
        fetchData(true);
    }, [fetchData]);

    // Récupération du nom du client
    useEffect(() => {
        if (id_client) {
            fetch(`${process.env.REACT_APP_API_URL}/get_client_data_by_id/${id_client}`)
                .then((response) => response.json())
                .then((data) => setDataClient(data))
                .catch((err) => setError(err.message));
        }
    }, [id_client]);

    // Récupération des données du bâtiment
    useEffect(() => {
        if (id_building) {
            fetch(`${process.env.REACT_APP_API_URL}/get_building/${id_building}`)
                .then((response) => response.json())
                .then((data) => {
                    setBuildingData(data);
                })
                .catch((err) => setError(err.message));
        }
    }, [id_building]);

    const handleSectionSubmit = async (event) => {
        event.preventDefault();

        // 1️⃣ Déterminer l'ID de l'unité fonctionnelle
        let functionalUnitId = sectionFunctionalUnit;

        // Créer une nouvelle unité fonctionnelle modifie un document existant
        // (building), ce qui reste hors scope offline v1 — l'option est déjà
        // désactivée dans le select quand !isOnline, ce garde est défensif.
        if (sectionFunctionalUnit === "new" && !isOnline) {
            toast.error("La création d'une nouvelle unité fonctionnelle nécessite une connexion.");
            return;
        }

        // Si l'utilisateur crée une nouvelle unité fonctionnelle
        if (sectionFunctionalUnit === "new" && newFunctionalUnit?.trim()) {
            const trimmedName = newFunctionalUnit.trim()
            const newId = generateFunctionalUnitId(trimmedName);

            // Ajouter la nouvelle unité au buildingData
            const updatedFunctionalUnits = [
            ...(buildingData.functional_units || []),
            { id: newId, name: trimmedName },
            ];

            try {
            const buildingUpdateResponse = await fetch(`${process.env.REACT_APP_API_URL}/update_building`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                ...buildingData,
                functional_units: updatedFunctionalUnits,
                }),
            });

            if (buildingUpdateResponse.ok) {
                toast.success("Nouvelle unité fonctionnelle ajoutée !");
                functionalUnitId = newId; // on sélectionne la nouvelle unité pour la section
                setBuildingData((prev) => ({
                ...prev,
                functional_units: updatedFunctionalUnits,
                }));
            } else {
                toast.error("Erreur lors de l'ajout de l'unité fonctionnelle.");
                return; // stop si échec
            }
            } catch (error) {
            console.error("Erreur API :", error);
            toast.error("Erreur de connexion à l'API.");
            return; // stop si échec
            }
        }

        // 2️⃣ Préparer les données de la section
        const sectionData = {
            _id: uuidv4(),
            id_building: id_building,
            name_section: sectionNameForm.trim(),
            // Renseignés ensuite si besoin, dans l'onglet Compléments de la modification.
            person_count: 0,
            frequentation: 'average',
            functional_unit: functionalUnitId,
        };

        // 3️⃣ Envoyer la section à l'API (ou la mettre en file d'attente hors-ligne)
        try {
            const result = await createSection(sectionData, { isOnline });

            // Mise à jour optimiste locale — identique online/offline. En ligne,
            // fetchData() ci-dessous la remplacera par la copie serveur (mêmes
            // données car l'_id est généré côté client).
            setSections((prev) => [...prev, sectionData]);
            setSelectedSection(sectionData);
            setSelectedRoom(null);

            // reset du formulaire
            setSectionNameForm('');
            setNewFunctionalUnit('');
            setSectionFunctionalUnit('');
            setShowSectionPopup(false);

            if (result.offline) {
                toast.info("Section enregistrée localement, en attente de synchronisation.");
            } else {
                fetchData(false, { sectionId: sectionData._id, roomId: null });
                toast.success("Section ajoutée avec succès.");
            }
        } catch (error) {
            console.error("Erreur API :", error);
            toast.error("Erreur de connexion à l'API");
        }
        };
    
    const handleRoomSubmit = async (event) => {
        event.preventDefault();

        // 1️⃣ Déterminer l'ID de l'unité fonctionnelle
        let functionalUnitId = roomFunctionalUnit;

        if (roomFunctionalUnit === "new" && !isOnline) {
            toast.error("La création d'une nouvelle unité fonctionnelle nécessite une connexion.");
            return;
        }

        // Si l'utilisateur crée une nouvelle unité fonctionnelle
        if (roomFunctionalUnit === "new" && newRoomFunctionalUnit?.trim()) {
            const trimmedName = newRoomFunctionalUnit.trim();
            const newId = generateFunctionalUnitId(trimmedName);

            // Ajouter la nouvelle unité au buildingData
            const updatedFunctionalUnits = [
                ...(buildingData.functional_units || []),
                { id: newId, name: trimmedName },
            ];

            try {
                const buildingUpdateResponse = await fetch(`${process.env.REACT_APP_API_URL}/update_building`, {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        ...buildingData,
                        functional_units: updatedFunctionalUnits,
                    }),
                });

                if (buildingUpdateResponse.ok) {
                    toast.success("Nouvelle unité fonctionnelle ajoutée !");
                    functionalUnitId = newId; // on sélectionne la nouvelle unité pour la room
                    setBuildingData((prev) => ({
                        ...prev,
                        functional_units: updatedFunctionalUnits,
                    }));
                } else {
                    toast.error("Erreur lors de l'ajout de l'unité fonctionnelle.");
                    return; // stop si échec
                }
            } catch (error) {
                console.error("Erreur API :", error);
                toast.error("Erreur de connexion à l'API.");
                return; // stop si échec
            }
        }

        // 2️⃣ Préparer les données de la room
        const roomData = {
            _id: uuidv4(),
            id_section: selectedSection._id,
            name_room: roomNameForm.trim(),
            // Renseignés ensuite si besoin, dans l'onglet Compléments de la modification.
            person_count: 0,
            frequentation: 'average',
            has_awareness_poster: roomHasAwarenessPoster,
            functional_unit: functionalUnitId,
            nb_identical_rooms: parseInt(roomNbIdenticalRooms, 10) || 1,
        };

        // 3️⃣ Envoyer la room à l'API (ou la mettre en file d'attente hors-ligne)
        try {
            const result = await createRoom(roomData, { isOnline });

            setRooms((prev) => [...prev, roomData]);
            setSelectedRoom(roomData);

            // reset du formulaire
            setRoomNameForm('');
            setRoomNbIdenticalRooms(1);
            setRoomFunctionalUnit('');
            setNewRoomFunctionalUnit('');
            setRoomHasAwarenessPoster(false);
            setShowRoomPopup(false);

            if (result.offline) {
                toast.info("Pièce enregistrée localement, en attente de synchronisation.");
            } else {
                fetchData(false, { sectionId: roomData.id_section, roomId: roomData._id });
                toast.success("Pièce ajoutée avec succès.");
            }
        } catch (error) {
            console.error("Erreur API :", error);
            toast.error("Erreur de connexion à l'API");
        }
    };



    const handleDeleteEquipment = async (id) => {
        const result = await Swal.fire({
            title: 'Confirmation de suppression ?',
            text: "Vous êtes sur le point de supprimer un équipement. Etes-vous sûr ?",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Oui, supprimer !',
            cancelButtonText: 'Annuler',
            customClass: 'swal-custom'
        });

        if (!result.isConfirmed) return;

        setLoading(true);
        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}/delete_equipment/${id}`, {
                method: 'DELETE',
            });

            if (response.ok) {
                setEquipments((prev) => prev.filter((equipment) => equipment._id !== id));
                toast.success("Equipement supprimé avec succès !")
            } else {
                toast.error("Erreur lors de la suppression de l'équipement.");
            }
        } catch (error) {
            toast.error("Erreur de connexion à l'API");
        }
        setLoading(false);
    };


    const handleDuplicateEquipment = async (equipment_id) => {
        if (!duplicateSection || !duplicateRoom) {
            Swal.fire({
                title: 'Erreur',
                text: "Veuillez sélectionner une section et une pièce.",
                icon: 'error',
                customClass: 'swal-custom'
            });
            return;
        }
    
        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}/duplicate_equipment`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    equipment_id,
                    section_id: duplicateSection._id,
                    room_id: duplicateRoom._id,
                }),
            });
    
            if (response.ok) {
                const data = await response.json();
                toast.success("Equipement dupliqué avec succès.\n Vérifiez à bien changer l'unité fonctionelle si elle correspond à la section initiale.");
                setShowDuplicatePopup(false);
                fetchData(); // Refresh the equipments list
            } else {
                toast.error("Erreur lors de la duplication de l'équipement.");
            }
        } catch (error) {
            toast.error("Errur de connexion à l'API");
        }
    };


    const handleEditSectionSubmit = async (event) => {
        event.preventDefault();

        let functionalUnitId = editSectionFunctionalUnit;

        // Si l'utilisateur veut créer une nouvelle unité fonctionnelle
        if (editSectionFunctionalUnit === "new" && newFunctionalUnit?.trim()) {
            const newId = generateFunctionalUnitId(newFunctionalUnit.trim());

            // Ajouter la nouvelle unité au buildingData
            const updatedFunctionalUnits = [
            ...(buildingData.functional_units || []),
            { id: newId, name: newFunctionalUnit.trim() },
            ];

            try {
            const buildingUpdateResponse = await fetch(`${process.env.REACT_APP_API_URL}/update_building`, {
                method: "PUT",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                ...buildingData,
                functional_units: updatedFunctionalUnits,
                }),
            });

            if (buildingUpdateResponse.ok) {
                toast.success("Nouvelle unité fonctionnelle ajoutée !");
                functionalUnitId = newId; // On sélectionne la nouvelle unité pour la section
                setBuildingData((prev) => ({
                ...prev,
                functional_units: updatedFunctionalUnits,
                }));
            } else {
                toast.error("Erreur lors de l'ajout de l'unité fonctionnelle.");
                return; // Stop si échec
            }
            } catch (error) {
            console.error("Erreur API :", error);
            toast.error("Erreur de connexion à l'API.");
            return; // Stop si échec
            }
        }

        // Si l'unité fonctionnelle de la section change réellement, vérifier si des pièces
        // ou des usages en dessous ont déjà une valeur différente et proposer de la propager.
        const previousSectionFunctionalUnit = selectedSection.functional_unit || '';
        if (functionalUnitId !== previousSectionFunctionalUnit) {
            try {
                const breakdownResponse = await fetch(`${process.env.REACT_APP_API_URL}/get_section_functional_unit_breakdown/${selectedSection._id}`);
                if (breakdownResponse.ok) {
                    const breakdown = await breakdownResponse.json();
                    const roomItems = (breakdown.rooms || []).map((r) => ({
                        _id: r._id, label: r.label, matchesOld: (r.functional_unit || '') === previousSectionFunctionalUnit,
                    }));
                    const usageItems = (breakdown.usages || []).map((u) => ({
                        _id: u._id, label: u.label, matchesOld: (u.functional_unit || '') === previousSectionFunctionalUnit,
                    }));
                    if (roomItems.length + usageItems.length > 0) {
                        setSectionCascadePrompt({ functionalUnitId, rooms: roomItems, usages: usageItems });
                        return; // On attend le choix de l'utilisateur dans la modale de cascade
                    }
                }
            } catch (error) {
                console.error("Erreur API :", error);
                // On ne bloque pas l'édition de la section si la vérification échoue
            }
        }

        await finalizeSectionEdit(functionalUnitId, null);
        };

    // Envoie la section modifiée, puis — si l'utilisateur a choisi de propager le changement
    // d'unité fonctionnelle — réaffecte les pièces/usages sélectionnés dans la même foulée.
    const finalizeSectionEdit = async (functionalUnitId, cascadeSelection) => {
        const sectionData = {
            name_section: editSectionNameForm.trim(),
            person_count: parseInt(editSectionPersonCount, 10) || 0,
            frequentation: editSectionFrequentation,
            functional_unit: functionalUnitId,
            notes: editSectionNotes,
            photos: editSectionPhotos,
        };

        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}/update_section/${selectedSection._id}`, {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(sectionData),
            });

            if (response.ok) {
            if (cascadeSelection && (cascadeSelection.room_ids.length || cascadeSelection.usage_ids.length)) {
                await fetch(`${process.env.REACT_APP_API_URL}/reassign_functional_unit`, {
                    method: "PUT",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ ...cascadeSelection, new_unit_id: functionalUnitId }),
                });
            }
            fetchData();
            setShowEditSectionPopup(false);
            setNewFunctionalUnit("");           // reset input
            setEditSectionFunctionalUnit("");   // reset select
            setSectionCascadePrompt(null);
            toast.success("Section mise à jour avec succès.");
            } else {
            toast.error("Erreur lors de la mise à jour.");
            }
        } catch (error) {
            console.error("Erreur API :", error);
            toast.error("Erreur de connexion à l'API");
        }
        };


    const handleDeleteSection = async () => {
        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}/delete_section/${selectedSection._id}`, {
                method: 'DELETE',
            });

            if (response.ok) {
                fetchData();
                setShowEditSectionPopup(false);
                toast.success("Section supprimée avec succès.");
            } else {
                toast.error("Erreur lors de la suppression.");
            }
        } catch (error) {
            toast.error("Errur de connexion à l'API");
        }
    };

    const handleEditRoomSubmit = async (event) => {
        event.preventDefault();

        let functionalUnitId = editRoomFunctionalUnit;

        // 🔹 Création d'une nouvelle unité fonctionnelle si nécessaire
        if (editRoomFunctionalUnit === "new" && newRoomFunctionalUnit?.trim()) {
            const newId = generateFunctionalUnitId(newRoomFunctionalUnit.trim());

            const updatedFunctionalUnits = [
                ...(buildingData.functional_units || []),
                { id: newId, name: newRoomFunctionalUnit.trim() },
            ];

            try {
                const buildingUpdateResponse = await fetch(
                    `${process.env.REACT_APP_API_URL}/update_building`,
                    {
                        method: "PUT",
                        headers: { "Content-Type": "application/json" },
                        body: JSON.stringify({
                            ...buildingData,
                            functional_units: updatedFunctionalUnits,
                        }),
                    }
                );

                if (buildingUpdateResponse.ok) {
                    toast.success("Nouvelle unité fonctionnelle ajoutée !");
                    functionalUnitId = newId;
                    setBuildingData((prev) => ({
                        ...prev,
                        functional_units: updatedFunctionalUnits,
                    }));
                } else {
                    toast.error("Erreur lors de l'ajout de l'unité fonctionnelle.");
                    return;
                }
            } catch (error) {
                console.error("Erreur API :", error);
                toast.error("Erreur de connexion à l'API.");
                return;
            }
        }

        // Si l'unité fonctionnelle de la pièce change réellement, vérifier si des usages en
        // dessous ont déjà une valeur différente et proposer de la propager.
        const previousRoomFunctionalUnit = selectedRoom.functional_unit || '';
        if (functionalUnitId !== previousRoomFunctionalUnit) {
            try {
                const breakdownResponse = await fetch(`${process.env.REACT_APP_API_URL}/get_room_functional_unit_breakdown/${selectedRoom._id}`);
                if (breakdownResponse.ok) {
                    const breakdown = await breakdownResponse.json();
                    const usageItems = (breakdown.usages || []).map((u) => ({
                        _id: u._id, label: u.label, matchesOld: (u.functional_unit || '') === previousRoomFunctionalUnit,
                    }));
                    if (usageItems.length > 0) {
                        setRoomCascadePrompt({ functionalUnitId, usages: usageItems });
                        return; // On attend le choix de l'utilisateur dans la modale de cascade
                    }
                }
            } catch (error) {
                console.error("Erreur API :", error);
                // On ne bloque pas l'édition de la pièce si la vérification échoue
            }
        }

        await finalizeRoomEdit(functionalUnitId, null);
    };

    // Envoie la pièce modifiée, puis — si l'utilisateur a choisi de propager le changement
    // d'unité fonctionnelle — réaffecte les usages sélectionnés dans la même foulée.
    const finalizeRoomEdit = async (functionalUnitId, cascadeSelection) => {
        const roomData = {
            name_room: editRoomNameForm.trim(),
            person_count: parseInt(editRoomPersonCount, 10) || 0,
            nb_identical_rooms: parseInt(editRoomNbIdenticalRooms, 10) || 1,
            frequentation: editRoomFrequentation,
            has_awareness_poster: editRoomHasAwarenessPoster,
            functional_unit: functionalUnitId,
            notes: editRoomNotes,
            photos: editRoomPhotos,
        };

        try {
            const response = await fetch(
                `${process.env.REACT_APP_API_URL}/update_room/${selectedRoom._id}`,
                {
                    method: "PUT",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify(roomData),
                }
            );

            if (response.ok) {
                if (cascadeSelection && cascadeSelection.usage_ids.length) {
                    await fetch(`${process.env.REACT_APP_API_URL}/reassign_functional_unit`, {
                        method: "PUT",
                        headers: { "Content-Type": "application/json" },
                        body: JSON.stringify({ ...cascadeSelection, new_unit_id: functionalUnitId }),
                    });
                }
                fetchData();
                setShowEditRoomPopup(false);
                setNewRoomFunctionalUnit("");      // reset input
                setEditRoomFunctionalUnit("");     // reset select
                setRoomCascadePrompt(null);
                toast.success("Pièce mise à jour avec succès.");
            } else {
                toast.error("Erreur lors de la mise à jour.");
            }
        } catch (error) {
            console.error("Erreur API :", error);
            toast.error("Erreur de connexion à l'API");
        }
    };


    const handleDeleteRoom = async () => {
        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}/delete_room/${selectedRoom._id}`, {
                method: 'DELETE',
            });

            if (response.ok) {
                fetchData();
                setShowEditRoomPopup(false);
                toast.success("Pièce supprimée avec succès.");
            } else {
                toast.error("Erreur lors de la suppression.");
            }
        } catch (error) {
            toast.error("Errur de connexion à l'API");
        }
    };

    const handleDuplicateSectionChange = async (sectionId) => {
        const section = sections.find(section => section._id === sectionId);
        setDuplicateSection(section);
        if (section) {
            try {
                const response = await fetch(`${process.env.REACT_APP_API_URL}/get_rooms_from_sections`, {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ sections: [section] })
                });

                if (response.ok) {
                    const data = await response.json();
                    setDuplicateRooms(data);
                } else {
                    toast.error("Erreur lors de la récupération des chambres");
                }
            } catch (error) {
                toast.error("Errur de connexion à l'API");
            }
        } else {
            setDuplicateRooms([]);
        }
    };

    return (
        <div className="container">
            <div className="sections-and-rooms-header">
                <h1>
                    {dataClient?.name_client && (
                        <span className="sections-and-rooms-client">{dataClient.name_client}</span>
                    )}
                    {buildingData?.name_building}
                </h1>
                <div className="sections-and-rooms-actions">
                    <button
                        onClick={backToBuildings}
                        className="back-button"
                        disabled={!isOnline}
                        title={!isOnline ? 'Nécessite une connexion' : undefined}
                    >
                        ← Bâtiments
                    </button>
                    {reorganizeMode ? (
                        <button
                            className="reorganize-button reorganize-button--done"
                            onClick={() => { setReorganizeMode(false); fetchData(); }}
                        >
                            ✓ Terminer
                        </button>
                    ) : (
                        <button
                            className="reorganize-button"
                            onClick={() => setReorganizeMode(true)}
                            disabled={!isOnline}
                            title={!isOnline ? 'Nécessite une connexion' : undefined}
                        >
                            ⇄ Réorganiser
                        </button>
                    )}
                    <button
                        onClick={goToDashboard}
                        className="dashboard-button"
                        disabled={!isOnline}
                        title={!isOnline ? 'Nécessite une connexion' : undefined}
                    >
                        Dashboard →
                    </button>
                </div>
            </div>
            {(!isOnline || queueCount > 0) && (
                <div className={`offline-banner${hasSyncErrors ? ' offline-banner--error' : ''}`}>
                    {!isOnline && <span>Hors-ligne</span>}
                    {syncProgress
                        ? <span>Synchronisation en cours… ({syncProgress.remaining} restants)</span>
                        : queueCount > 0 && <span>{queueCount} élément{queueCount > 1 ? 's' : ''} en attente de synchronisation</span>}
                    {hasSyncErrors && <span>Échec de synchronisation :</span>}
                    {syncAuthRequired && <span>Reconnexion nécessaire pour synchroniser.</span>}
                    {isOnline && queueCount > 0 && !syncProgress && !syncAuthRequired && (
                        <button className="offline-banner-retry" onClick={retrySync}>
                            {hasSyncErrors ? 'réessayer' : 'envoyer maintenant'}
                        </button>
                    )}
                    {storageEstimateSupported && usageBytes != null && quotaBytes != null && (
                        <span className="offline-banner-storage">
                            {formatMB(usageBytes)} utilisés sur {formatMB(quotaBytes)} disponibles
                        </span>
                    )}
                </div>
            )}
            {reorganizeMode && !loading && (
                <ReorganizeView
                    sections={sections}
                    rooms={rooms}
                    equipments={equipments}
                    idBuilding={id_building}
                    buildingData={buildingData}
                />
            )}

            {!reorganizeMode && (<>
            <div className="audit-subsection-title">
                <span className="audit-subsection-bar"></span>
                <h2>Sections</h2>
            </div>
            <div>
                {loading ? (
                    <div className="loading-spinner">
                        <WalterLoader size={56} />
                    </div>
                ) : (
                    <div className="sections-rooms-buttons-container">
                        {sections.map((section) => (
                            <div
                                key={section._id}
                                className={`section-div ${selectedSection === section ? 'active' : ''}`}
                                onClick={() => handleSectionClick(section)}
                                onDoubleClick={() => isOnline && handleSectionDoubleClick(section)}
                            >
                                {section?.name_section}
                                {pendingSyncIds[section._id] && (
                                    <span
                                        className={`sync-pending-dot${pendingSyncIds[section._id] === 'error' ? ' sync-pending-dot--error' : ''}`}
                                        title={pendingSyncIds[section._id] === 'error' ? 'Échec de synchronisation' : 'En attente de synchronisation'}
                                    />
                                )}
                            </div>
                        ))}
                        <div
                            className="section-div add-section-button"
                            onClick={() => setShowSectionPopup(true)}
                        >
                            + Section
                        </div>
                    </div>
                )}
            </div>
            <div className="audit-subsection-title">
                <span className="audit-subsection-bar"></span>
                <h2>Pièces : {selectedSection ? selectedSection.name_section : "aucune section sélectionnée"}</h2>
            </div>
            <div>
                {loading ? (
                    <div className="loading-spinner">
                        <WalterLoader size={56} />
                    </div>
                ) : (
                    <div className="sections-rooms-buttons-container">
                        {rooms.filter(room => room.id_section === selectedSection?._id).map((room) => (
                            <div
                                key={room._id}
                                className={`room-div ${selectedRoom === room ? 'active' : ''}`}
                                onClick={() => setSelectedRoom(room)}
                                onDoubleClick={() => isOnline && handleRoomDoubleClick(room)}
                            >
                                {room?.name_room}
                                {room?.nb_identical_rooms > 1 && (
                                    <span
                                        className="room-multiplier-badge"
                                        title={`Cette pièce représente ${room.nb_identical_rooms} pièces identiques`}
                                    >
                                        ×{room.nb_identical_rooms}
                                    </span>
                                )}
                                {pendingSyncIds[room._id] && (
                                    <span
                                        className={`sync-pending-dot${pendingSyncIds[room._id] === 'error' ? ' sync-pending-dot--error' : ''}`}
                                        title={pendingSyncIds[room._id] === 'error' ? 'Échec de synchronisation' : 'En attente de synchronisation'}
                                    />
                                )}
                            </div>
                        ))}
                        <div
                            className="room-div add-room-button"
                            onClick={() => {
                                setRoomFunctionalUnit(selectedSection?.functional_unit || '');
                                setShowRoomPopup(true)}}
                        >
                            + Pièce
                        </div>
                    </div>
                )}
            </div>
            <div className="audit-subsection-title">
                <span className="audit-subsection-bar"></span>
                <h2>Équipements : {selectedRoom ? selectedRoom.name_room : "aucune pièce sélectionnée"}</h2>
            </div>

            <div className="equipments-table">
                {loading ? (
                    <div className="loading-spinner">
                        <WalterLoader size={56} />
                    </div>
                ) : (
                    selectedRoom && equipments.filter(equipment => equipment.location.room_id === selectedRoom._id).length > 0 ? (
                        <table>
                            <thead>
                                <tr>
                                    <th>Type d'équipement</th>
                                    <th>Usages</th>
                                    <th>Nombre</th>
                                    <th>Valeur d'eau</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {equipments
                                    .filter(equipment => equipment.location.room_id === selectedRoom?._id) // Filter by selected room
                                    .map((equipment) => (
                                        <tr
                                            key={equipment._id}
                                            className={isOnline ? 'equipment-row-clickable' : undefined}
                                            onClick={() => {
                                                // Pas d'ouverture si l'utilisateur surligne une valeur pour la copier
                                                if (!isOnline || window.getSelection()?.toString()) return;
                                                handleEditEquipment(selectedSection._id, selectedRoom._id, equipment._id);
                                            }}
                                        >
                                            <td>
                                                {translate(equipment.type_equipment)}
                                                {pendingSyncIds[equipment._id] && (
                                                    <span
                                                        className={`sync-pending-dot${pendingSyncIds[equipment._id] === 'error' ? ' sync-pending-dot--error' : ''}`}
                                                        title={pendingSyncIds[equipment._id] === 'error' ? 'Échec de synchronisation' : 'En attente de synchronisation'}
                                                    />
                                                )}
                                            </td>
                                            <td dangerouslySetInnerHTML={{ __html: equipment.usages?.join("<br>") || "Aucun usage" }}></td>
                                            <td>{equipment.technical_data?.nb_equipment}</td>
                                            <td>{equipment.water_value} {equipment.water_value_unit}</td>
                                            <td className='edit-delete-icons' onClick={(e) => e.stopPropagation()}>
                                                <FaEdit
                                                    className={`edit-icon${!isOnline ? ' icon-disabled' : ''}`}
                                                    onClick={() => isOnline && handleEditEquipment(selectedSection._id, selectedRoom._id, equipment._id)}
                                                    size={16}
                                                    title={!isOnline ? 'Nécessite une connexion' : undefined}
                                                />
                                                <FaTrash
                                                    className={`delete-icon${!isOnline ? ' icon-disabled' : ''}`}
                                                    onClick={() => isOnline && handleDeleteEquipment(equipment._id)}
                                                    size={16}
                                                    title={!isOnline ? 'Nécessite une connexion' : undefined}
                                                />
                                                <FaClone
                                                    className={`duplicate-icon${!isOnline ? ' icon-disabled' : ''}`}
                                                    onClick={() => { if (isOnline) { setSelectedEquipment(equipment); setShowDuplicatePopup(true); } }}
                                                    size={16}
                                                    title={!isOnline ? 'Nécessite une connexion' : undefined}
                                                />
                                            </td>
                                        </tr>
                                    ))}
                            </tbody>
                        </table>
                    ) : (
                        <p>Aucun équipement disponible pour cette pièce.</p>
                    )
                )}
            </div>
            <div className="add-equipment-button-container">
                <button onClick={() => selectedSection && selectedRoom && addEquipment(selectedSection._id, selectedRoom._id)} className="add-equipment-button">
                    + Ajouter un équipement
                </button>
            </div>
            </>)}

            {showSectionPopup && (
                <div className="section-popup">
                    <div className="section-popup-content">
                        <span className="close" onClick={() => setShowSectionPopup(false)}>&times;</span>
                        <h2>Ajouter une nouvelle section</h2>
                        <form onSubmit={handleSectionSubmit}>
                            <label htmlFor="sectionName">Nom de la section</label>
                            <input
                                id="sectionName"
                                type="text"
                                value={sectionNameForm}
                                onChange={(e) => setSectionNameForm(e.target.value)}
                                required
                            />
                            <label htmlFor="sectionFunctionalUnit">Unité fonctionnelle</label>
                            <select
                                id="sectionFunctionalUnit"
                                value={sectionFunctionalUnit}
                                onChange={(e) => {
                                setSectionFunctionalUnit(e.target.value);
                                if (e.target.value !== "new") setNewFunctionalUnit(""); // reset si on sélectionne autre chose
                                }}
                                required
                            >
                                <option value="">Sélectionner une unité fonctionnelle</option>

                                {buildingData.functional_units &&
                                buildingData.functional_units.map((unit) => (
                                    <option key={unit.id} value={unit.id}>
                                    {unit.name}
                                    </option>
                                ))}

                                <option value="new" disabled={!isOnline}>
                                    {isOnline ? '+ Ajouter une nouvelle unité' : '+ Ajouter une nouvelle unité (nécessite une connexion)'}
                                </option>
                            </select>

                            {sectionFunctionalUnit === "new" && (
                                <input
                                type="text"
                                placeholder="Nom de la nouvelle unité"
                                value={newFunctionalUnit}
                                onChange={(e) => setNewFunctionalUnit(e.target.value)}
                                required
                                />
                            )}

                            <button type="submit" className="add-equipment-button">Ajouter</button>
                        </form>
                    </div>
                </div>
            )}
            {showRoomPopup && (
                <div className="section-popup">
                    <div className="section-popup-content">
                        <span className="close" onClick={() => setShowRoomPopup(false)}>&times;</span>
                        <h2>Ajouter une nouvelle pièce</h2>
                        <form onSubmit={handleRoomSubmit}>
                            <label htmlFor="roomName">Nom de la pièce</label>
                            <input
                                id="roomName"
                                type="text"
                                value={roomNameForm}
                                onChange={(e) => setRoomNameForm(e.target.value)}
                                required
                            />
                            <label htmlFor="roomFunctionalUnit">Unité fonctionnelle</label>
                            <select
                                id="roomFunctionalUnit"
                                value={roomFunctionalUnit}
                                onChange={(e) => {
                                    setRoomFunctionalUnit(e.target.value);
                                    if (e.target.value !== "new") setNewRoomFunctionalUnit(""); // reset si on sélectionne autre chose
                                }}
                            >
                                <option value="">Sélectionner une unité fonctionnelle</option>

                                {buildingData.functional_units.map((fu) => (
                                    <option key={fu.id} value={fu.id}>
                                        {fu.name}
                                    </option>
                                ))}

                                <option value="new" disabled={!isOnline}>
                                    {isOnline ? '+ Ajouter une nouvelle unité' : '+ Ajouter une nouvelle unité (nécessite une connexion)'}
                                </option>
                            </select>

                            {/* Champ pour la nouvelle unité */}
                            {roomFunctionalUnit === "new" && (
                                <input
                                    type="text"
                                    placeholder="Nom de la nouvelle unité"
                                    value={newRoomFunctionalUnit}
                                    onChange={(e) => setNewRoomFunctionalUnit(e.target.value)}
                                    required
                                />
                            )}



                            <label htmlFor="roomNbIdenticalRooms">Nombre de pièces identiques</label>
                            <input
                                id="roomNbIdenticalRooms"
                                type="number"
                                min="1"
                                value={roomNbIdenticalRooms}
                                onChange={(e) => setRoomNbIdenticalRooms(e.target.value)}
                                title="Si cette pièce se répète à l'identique (ex : 50 chambres strictement identiques), indiquez le nombre total ici plutôt que de la dupliquer. Les points d'eau ajoutés ci-dessous seront comptés pour une seule pièce ; les volumes et coûts seront automatiquement multipliés."
                            />
                            <label className="checkbox-label">
                                <input
                                    type="checkbox"
                                    checked={roomHasAwarenessPoster}
                                    onChange={(e) => setRoomHasAwarenessPoster(e.target.checked)}
                                />
                                Y a-t-il une affiche de sensibilisation ?
                            </label>
                            <button type="submit" className="add-equipment-button">Ajouter</button>
                        </form>
                    </div>
                </div>
            )}
            {showEditSectionPopup && (
                <div className="section-popup">
                    <div className="section-popup-content">
                        <span className="close" onClick={closeEditSectionPopup}>&times;</span>
                        <h2>Modifier la section</h2>
                        <div className="popup-tabs">
                            <button
                                type="button"
                                className={`popup-tab${editSectionActiveTab === 'info' ? ' popup-tab--active' : ''}`}
                                onClick={() => setEditSectionActiveTab('info')}
                            >
                                Informations
                            </button>
                            <button
                                type="button"
                                className={`popup-tab${editSectionActiveTab === 'notes' ? ' popup-tab--active' : ''}`}
                                onClick={() => setEditSectionActiveTab('notes')}
                            >
                                Compléments
                            </button>
                        </div>
                        {editSectionActiveTab === 'notes' ? (
                            <form onSubmit={handleEditSectionSubmit}>
                                <label htmlFor="editSectionPersonCount">Nombre de personnes</label>
                                <input
                                    id="editSectionPersonCount"
                                    type="number"
                                    value={editSectionPersonCount || ''}
                                    onChange={(e) => setEditSectionPersonCount(e.target.value)}
                                />
                                <label htmlFor="editSectionFrequentation">Fréquentation</label>
                                <select
                                    id="editSectionFrequentation"
                                    value={editSectionFrequentation}
                                    onChange={(e) => setEditSectionFrequentation(e.target.value)}
                                >
                                    <option value="low">Faible</option>
                                    <option value="average">Moyenne</option>
                                    <option value="high">Forte</option>
                                </select>
                                <NotesAndPhotosSection
                                    notes={editSectionNotes}
                                    onNotesChange={setEditSectionNotes}
                                    photos={editSectionPhotos}
                                    onPhotosChange={setEditSectionPhotos}
                                />
                                <button type="submit" className="add-equipment-button">Modifier</button>
                            </form>
                        ) : (
                        <form onSubmit={handleEditSectionSubmit}>
                            <label htmlFor="editSectionName">Nom de la section</label>
                            <input
                                id="editSectionName"
                                type="text"
                                value={editSectionNameForm}
                                onChange={(e) => setEditSectionNameForm(e.target.value)}
                                required
                            />
                            <label htmlFor="editSectionFunctionalUnit">Unité fonctionnelle</label>
                            <select
                            id="editSectionFunctionalUnit"
                            value={editSectionFunctionalUnit || ''} // état qui contient l'ID actuel de l'unité
                            onChange={(e) => {
                                setEditSectionFunctionalUnit(e.target.value);
                                if (e.target.value !== "new") setNewFunctionalUnit(""); // reset input si ce n'est pas "new"
                            }}
                            required
                            >
                            <option value="">Sélectionner une unité fonctionnelle</option>

                            {buildingData.functional_units &&
                                buildingData.functional_units.map((unit) => (
                                <option key={unit.id} value={unit.id}>
                                {unit.name}
                                </option>
                                ))}

                            {/* Option pour créer une nouvelle unité */}
                            <option value="new">+ Nouvelle unité fonctionnelle</option>
                            </select>

                            {/* Input pour la nouvelle unité fonctionnelle si l'utilisateur choisit "new" */}
                            {editSectionFunctionalUnit === "new" && (
                            <input
                                type="text"
                                placeholder="Nom de la nouvelle unité"
                                value={newFunctionalUnit || ""}
                                onChange={(e) => setNewFunctionalUnit(e.target.value)}
                                required
                            />
                            )}


                            <div className='two-buttons'>
                                <button type="submit" className="add-equipment-button">Modifier</button>
                                <button type="button" className="duplicate-button-popup" onClick={() => startDuplicate('section')}>⧉ Dupliquer</button>
                                <button type="button" className="delete-button-popup" onClick={handleDeleteSection}>Supprimer</button>
                            </div>
                        </form>
                        )}
                    </div>
                </div>
            )}

            {showEditRoomPopup && (
                <div className="section-popup">
                    <div className="section-popup-content">
                        <span className="close" onClick={closeEditRoomPopup}>&times;</span>
                        <h2>Modifier la pièce</h2>
                        <div className="popup-tabs">
                            <button
                                type="button"
                                className={`popup-tab${editRoomActiveTab === 'info' ? ' popup-tab--active' : ''}`}
                                onClick={() => setEditRoomActiveTab('info')}
                            >
                                Informations
                            </button>
                            <button
                                type="button"
                                className={`popup-tab${editRoomActiveTab === 'notes' ? ' popup-tab--active' : ''}`}
                                onClick={() => setEditRoomActiveTab('notes')}
                            >
                                Compléments
                            </button>
                        </div>
                        {editRoomActiveTab === 'notes' ? (
                            <form onSubmit={handleEditRoomSubmit}>
                                <label htmlFor="editRoomPersonCount">Nombre de personnes</label>
                                <input
                                    id="editRoomPersonCount"
                                    type="number"
                                    value={editRoomPersonCount || ''}
                                    onChange={(e) => setEditRoomPersonCount(e.target.value)}
                                />
                                <label htmlFor="editRoomFrequentation">Fréquentation</label>
                                <select
                                    id="editRoomFrequentation"
                                    value={editRoomFrequentation}
                                    onChange={(e) => setEditRoomFrequentation(e.target.value)}
                                >
                                    <option value="low">Faible</option>
                                    <option value="average">Moyenne</option>
                                    <option value="high">Forte</option>
                                </select>
                                <NotesAndPhotosSection
                                    notes={editRoomNotes}
                                    onNotesChange={setEditRoomNotes}
                                    photos={editRoomPhotos}
                                    onPhotosChange={setEditRoomPhotos}
                                />
                                <button type="submit" className="add-equipment-button">Modifier</button>
                            </form>
                        ) : (
                        <form onSubmit={handleEditRoomSubmit}>
                            <label htmlFor="editRoomName">Nom de la pièce</label>
                            <input
                                id="editRoomName"
                                type="text"
                                value={editRoomNameForm}
                                onChange={(e) => setEditRoomNameForm(e.target.value)}
                                required
                            />
                            <label htmlFor="editRoomFunctionalUnit">Unité fonctionnelle</label>
                            <select
                                id="editRoomFunctionalUnit"
                                value={editRoomFunctionalUnit || ''} // état actuel de l'unité
                                onChange={(e) => {
                                    setEditRoomFunctionalUnit(e.target.value);
                                    if (e.target.value !== "new") setNewRoomFunctionalUnit(""); // reset si ce n'est pas "new"
                                }}
                                required
                            >
                                <option value="">Sélectionner une unité fonctionnelle</option>

                                {buildingData.functional_units.map((fu) => (
                                    <option key={fu.id} value={fu.id}>
                                        {fu.name}
                                    </option>
                                ))}

                                {/* Option pour créer une nouvelle unité */}
                                <option value="new">+ Nouvelle unité fonctionnelle</option>
                            </select>

                            {/* Input pour la nouvelle unité fonctionnelle */}
                            {editRoomFunctionalUnit === "new" && (
                                <input
                                    type="text"
                                    placeholder="Nom de la nouvelle unité"
                                    value={newRoomFunctionalUnit || ""}
                                    onChange={(e) => setNewRoomFunctionalUnit(e.target.value)}
                                    required
                                />
                            )}
                            <label htmlFor="editRoomNbIdenticalRooms">Nombre de pièces identiques</label>
                            <input
                                id="editRoomNbIdenticalRooms"
                                type="number"
                                min="1"
                                value={editRoomNbIdenticalRooms}
                                onChange={(e) => setEditRoomNbIdenticalRooms(e.target.value)}
                                title="Si cette pièce se répète à l'identique (ex : 50 chambres strictement identiques), indiquez le nombre total ici plutôt que de la dupliquer. Les points d'eau ajoutés ci-dessous seront comptés pour une seule pièce ; les volumes et coûts seront automatiquement multipliés."
                            />
                            <label className="checkbox-label">
                                <input
                                    type="checkbox"
                                    checked={editRoomHasAwarenessPoster}
                                    onChange={(e) => setEditRoomHasAwarenessPoster(e.target.checked)}
                                />
                                Y a-t-il une affiche de sensibilisation ?
                            </label>
                            <div className='two-buttons'>
                                <button type="submit" className="add-equipment-button">Modifier</button>
                                <button type="button" className="duplicate-button-popup" onClick={() => startDuplicate('room')}>⧉ Dupliquer</button>
                                <button type="button" className="delete-button-popup" onClick={handleDeleteRoom}>Supprimer</button>
                            </div>
                        </form>
                        )}
                    </div>
                </div>
            )}
            {showDuplicatePopup && (
                <div className="section-popup">
                    <div className="section-popup-content">
                        <span className="close" onClick={() => setShowDuplicatePopup(false)}>&times;</span>
                        <h2>Dupliquer l'équipement dans</h2>
                        <div>
                            <label>Section:</label>
                            <select onChange={(e) => handleDuplicateSectionChange(e.target.value)}>
                                <option value="">Séléctionner une section</option>
                                {sections.map(section => (
                                    <option key={section._id} value={section._id}>{section.name_section}</option>
                                ))}
                            </select>
                        </div>
                        <div>
                            <label>Pièce:</label>
                            <select onChange={(e) => setDuplicateRoom(duplicateRooms.find(room => room._id === e.target.value))}>
                                <option value="">Séléctionner une pièce</option>
                                {duplicateRooms.map(room => (
                                    <option key={room._id} value={room._id}>{room.name_room}</option>
                                ))}
                            </select>
                        </div>
                        <button onClick={() => handleDuplicateEquipment(selectedEquipment._id)} className="add-equipment-button">Dupliquer</button>
                    </div>
                </div>
            )}
            {duplicateZone && (
                <DuplicateZoneModal
                    kind={duplicateZone.kind}
                    item={duplicateZone.item}
                    sections={sections}
                    equipments={equipments}
                    translate={translate}
                    onClose={() => setDuplicateZone(null)}
                    onDone={handleDuplicateDone}
                />
            )}
            {sectionCascadePrompt && (
                <FunctionalUnitCascadeModal
                    title="Propager le changement à la section ?"
                    description="Des pièces et/ou usages de cette section ont déjà une unité fonctionnelle différente de celle de la section. Choisissez ceux à faire suivre vers la nouvelle unité (les autres restent inchangés)."
                    groups={[
                        { key: 'rooms', label: 'Pièces', items: sectionCascadePrompt.rooms },
                        { key: 'usages', label: 'Usages', items: sectionCascadePrompt.usages },
                    ]}
                    fixedTargetLabel={buildingData?.functional_units?.find((u) => u.id === sectionCascadePrompt.functionalUnitId)?.name || 'Aucune (vider)'}
                    defaultAllChecked={false}
                    onConfirm={(selection) => finalizeSectionEdit(sectionCascadePrompt.functionalUnitId, { section_ids: [], room_ids: selection.rooms, usage_ids: selection.usages })}
                    onSkip={() => finalizeSectionEdit(sectionCascadePrompt.functionalUnitId, null)}
                    onCancel={() => setSectionCascadePrompt(null)}
                />
            )}
            {roomCascadePrompt && (
                <FunctionalUnitCascadeModal
                    title="Propager le changement à la pièce ?"
                    description="Des usages de cette pièce ont déjà une unité fonctionnelle différente de celle de la pièce. Choisissez ceux à faire suivre vers la nouvelle unité (les autres restent inchangés)."
                    groups={[
                        { key: 'usages', label: 'Usages', items: roomCascadePrompt.usages },
                    ]}
                    fixedTargetLabel={buildingData?.functional_units?.find((u) => u.id === roomCascadePrompt.functionalUnitId)?.name || 'Aucune (vider)'}
                    defaultAllChecked={false}
                    onConfirm={(selection) => finalizeRoomEdit(roomCascadePrompt.functionalUnitId, { section_ids: [], room_ids: [], usage_ids: selection.usages })}
                    onSkip={() => finalizeRoomEdit(roomCascadePrompt.functionalUnitId, null)}
                    onCancel={() => setRoomCascadePrompt(null)}
                />
            )}
            <ToastContainer />

            {showEquipmentSheet && (
                <>
                    <div className="sheet-backdrop" />
                    <div className="sheet-container">
                        <div className="sheet-header">
                            <div className="sheet-handle" />
                            <button
                                className="sheet-close-btn"
                                onClick={() => waterpointRef.current?.triggerClose()}
                                disabled={sheetIsSaving}
                            >
                                ✕
                            </button>
                        </div>
                        <div className="sheet-body">
                            <WaterpointForm
                                ref={waterpointRef}
                                key={sheetKey}
                                sectionId={sheetSectionId}
                                roomId={sheetRoomId}
                                equipmentId={sheetEquipmentId}
                                onClose={handleSheetClose}
                                onCanSaveChange={setSheetCanSave}
                                onSavingChange={setSheetIsSaving}
                                buildingData={buildingData}
                                roomData={rooms.find(r => r._id === sheetRoomId)}
                            />
                        </div>
                        <div className="sheet-footer">
                            <button
                                className={`sheet-save-btn${(sheetCanSave && !sheetIsSaving) ? '' : ' sheet-save-btn--disabled'}`}
                                onClick={() => waterpointRef.current?.triggerSave()}
                                disabled={!sheetCanSave || sheetIsSaving}
                            >
                                {sheetIsSaving && <span className="btn-spinner" />}
                                {sheetIsSaving ? 'Enregistrement...' : "Enregistrer l'équipement"}
                            </button>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
}

export default AuditPageCreationSectionsAndRooms;